/**
 * Shared Normalization Utilities
 *
 * Common functions for normalizing data from Firestore and external sources
 */

import type { PlanTier, UserStatus, AdminUserPlanInfo, Location, Coordinates } from '@my-roadtrip/types'
import { isString, isNonEmptyString } from './type-guards.js'

// ============================================================================
// Type Definitions
// ============================================================================

/** Firestore Timestamp-like object */
interface FirestoreTimestampLike {
  toDate: () => Date
  seconds?: number
  nanoseconds?: number
}

/** Generic coordinate source that may have various formats */
interface CoordinateSource {
  lat?: unknown
  lng?: unknown
  latitude?: unknown
  longitude?: unknown
  _lat?: unknown
  _long?: unknown
  coordinates?: {
    lat?: unknown
    lng?: unknown
    latitude?: unknown
    longitude?: unknown
  }
}

/**
 * Normalize a date value to ISO string
 */
export function toIsoDate(value: unknown): string | null {
  if (!value) return null

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === 'number') {
    return new Date(value).toISOString()
  }

  if (typeof value === 'string') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString()
    }
  }

  // Firestore Timestamp-like
  if (typeof (value as any)?.toDate === 'function') {
    try {
      return ((value as any).toDate() as Date).toISOString()
    } catch {
      return null
    }
  }

  return null
}

/**
 * Normalize a date value to Date object
 */
export function toDateSafe(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return value

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  if (typeof (value as { toDate?: () => Date })?.toDate === 'function') {
    try {
      const converted = (value as { toDate: () => Date }).toDate()
      return converted instanceof Date ? converted : null
    } catch {
      return null
    }
  }

  return null
}

/**
 * Normalize plan tier value
 */
export function normalizePlanTier(value: unknown): PlanTier {
  if (!isString(value)) return 'free'

  const lower = value.toLowerCase()
  if (lower === 'premium') return 'premium'
  if (lower === 'pro') return 'pro'
  return 'free'
}

/**
 * Normalize user status value
 */
export function normalizeUserStatus(status: unknown): UserStatus {
  if (status === 'suspended' || status === 'banned' || status === 'active') {
    return status
  }
  return 'active'
}

/**
 * Normalize plan info object
 */
export function normalizePlanInfo(data: Record<string, unknown>): AdminUserPlanInfo {
  const planId = isString(data.planId) ? data.planId : undefined

  let rawTier = 'free'
  if (isString(data.planTier)) {
    rawTier = data.planTier
  } else if (isString(data.plan)) {
    rawTier = data.plan
  }

  const tier = normalizePlanTier(rawTier)

  let name = 'Free'
  if (isString(data.planName)) {
    name = data.planName
  } else if (tier === 'premium') {
    name = 'Premium'
  } else if (tier === 'pro') {
    name = 'Pro'
  }

  const result: AdminUserPlanInfo = { tier, name }
  if (planId) result.id = planId

  return result
}

/**
 * Coerce value to number or return null
 */
export function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { toNumber?: () => number }).toNumber === 'function'
  ) {
    const result = (value as { toNumber: () => number }).toNumber()
    return Number.isFinite(result) ? result : null
  }

  return null
}

/**
 * Convert value to number with fallback
 */
export function toNumber(value: unknown, fallback = 0): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

/**
 * Get first valid number from array of candidates
 */
export function firstNumber(values: unknown[]): number | null {
  for (const candidate of values) {
    const coerced = coerceNumber(candidate)
    if (coerced !== null) {
      return coerced
    }
  }
  return null
}

/**
 * Normalize date with fallback to current time
 * (Useful when you always want a valid Date)
 */
export function toDateOrNow(value?: unknown): Date {
  const result = toDateSafe(value)
  return result ?? new Date()
}

/**
 * Sanitize string: trim + remove non-allowed characters
 */
export function sanitizeDisplayName(value: unknown): string {
  if (!isString(value)) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  // Keep letters, numbers, spaces, hyphens, apostrophes
  return trimmed.replace(/[^\p{L}\p{N}\s'-]/gu, '')
}

/**
 * Sanitize array of strings, dedupe, limit length
 */
export function sanitizeStringArray(
  value: unknown,
  options: { maxLength?: number; lowercase?: boolean } = {}
): string[] {
  const { maxLength = 10, lowercase = false } = options
  if (!Array.isArray(value)) return []

  const set = new Set<string>()
  for (const entry of value) {
    if (isString(entry)) {
      const sanitized = lowercase ? entry.trim().toLowerCase() : entry.trim()
      if (sanitized.length > 0) {
        set.add(sanitized)
      }
    }
  }
  return Array.from(set).slice(0, maxLength)
}

// ============================================================================
// Date Normalization Extensions
// ============================================================================

/**
 * Normalize date to Date or undefined (useful for optional date fields)
 * Unlike toDateSafe which returns null, this returns undefined for missing/invalid values
 */
export function toDateOrUndefined(value: unknown): Date | undefined {
  const result = toDateSafe(value)
  return result ?? undefined
}

/**
 * Convert value to timestamp (milliseconds since epoch)
 * Returns 0 for invalid/missing values
 */
export function toTimestamp(value: unknown): number {
  if (!value) return 0
  if (value instanceof Date) return value.getTime()

  if (typeof (value as FirestoreTimestampLike)?.toDate === 'function') {
    try {
      return (value as FirestoreTimestampLike).toDate().getTime()
    } catch {
      return 0
    }
  }

  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  return 0
}

// ============================================================================
// Coordinate & Location Normalization
// ============================================================================

/**
 * Extract coordinates from various source formats
 *
 * Handles:
 * - Direct lat/lng properties
 * - latitude/longitude properties
 * - Nested coordinates object
 * - Firestore GeoPoint (_lat/_long)
 *
 * @returns Coordinates object or null if extraction fails
 */
export function extractCoordinates(source: unknown): Coordinates | null {
  if (!source || typeof source !== 'object') {
    return null
  }

  const obj = source as CoordinateSource

  // Try nested coordinates first
  const coords = obj.coordinates
  if (coords && typeof coords === 'object') {
    const lat = firstNumber([coords.lat, coords.latitude])
    const lng = firstNumber([coords.lng, coords.longitude])
    if (lat !== null && lng !== null && isValidLatLng(lat, lng)) {
      return { lat, lng }
    }
  }

  // Try direct properties
  const lat = firstNumber([obj.lat, obj.latitude, obj._lat])
  const lng = firstNumber([obj.lng, obj.longitude, obj._long])

  if (lat !== null && lng !== null && isValidLatLng(lat, lng)) {
    return { lat, lng }
  }

  return null
}

/**
 * Validate latitude and longitude values
 */
export function isValidLatLng(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  )
}

/**
 * Normalize a raw location object to Location type
 *
 * Handles legacy formats, missing fields, and invalid coordinates.
 * Uses NaN for missing coordinates so JSON serializes to null.
 */
export function normalizeLocation(raw: unknown): Location {
  const defaultCoordinates: Coordinates = { lat: Number.NaN, lng: Number.NaN }

  if (!raw || typeof raw !== 'object') {
    return {
      address: '',
      coordinates: defaultCoordinates
    }
  }

  const payload = raw as Record<string, unknown>

  // Extract coordinates using the unified extractor
  const extracted = extractCoordinates(raw)
  const coordinates: Coordinates = extracted ?? defaultCoordinates

  // Extract address
  const address =
    isNonEmptyString(payload.address)
      ? payload.address.trim()
      : ''

  // Build normalized location
  const normalized: Location = {
    address,
    coordinates
  }

  // Optional fields
  if (isNonEmptyString(payload.placeId)) {
    normalized.placeId = payload.placeId
  }
  if (isNonEmptyString(payload.name)) {
    normalized.name = payload.name.trim()
  }

  // Legacy compatibility: also set top-level lat/lng
  normalized.lat = coordinates.lat
  normalized.lng = coordinates.lng

  return normalized
}

/**
 * Pick first valid numeric value from candidates
 * Handles Firestore Timestamp seconds/nanoseconds format
 */
export function pickFirstNumeric(...values: unknown[]): number | null {
  for (const candidate of values) {
    const numeric = coerceNumberExtended(candidate)
    if (numeric !== null) {
      return numeric
    }
  }
  return null
}

/**
 * Extended number coercion that handles Firestore timestamp objects
 */
function coerceNumberExtended(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (isNonEmptyString(value)) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>

    // Handle Firestore Timestamp {seconds, nanoseconds}
    if ('seconds' in record && typeof record.seconds === 'number') {
      const seconds = record.seconds
      const nanos = typeof record.nanoseconds === 'number' ? record.nanoseconds : 0
      return seconds + nanos / 1_000_000_000
    }

    // Handle toNumber method
    if (typeof (value as { toNumber?: () => number }).toNumber === 'function') {
      const result = (value as { toNumber: () => number }).toNumber()
      return Number.isFinite(result) ? result : null
    }
  }

  return null
}
